import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Betrag } from './Betrag'
import { Abrufausfall } from './Abruf'
import { api, betrag, imHintergrund, periodenname } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { monatFehlt, useMonatsfrage } from './Wirksamkeit'
import { Auswahlfeld, ausStammdatum, mitCode } from './Auswahlfeld'
import {
  anwendername,
  anzahlwort,
  belegtext,
  beschriftung,
  bewertungsebenenname,
  datenquellenname,
  gesellschaftsname,
  kontobezeichnung,
  kreisname,
  mitNamen,
  t,
  zeitpunkt,
} from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { Spaltenkopf } from './Journalsuche'
import { abbildungLesen } from './Datenquellen'
import { Packageliste } from './Package'
import { Stapelliste } from './Stapel'
import { Sammelaktualisierung } from './Sammelaktualisierung'
import { Vorschlagsfrage, alleBeantwortet, mitBisherigenAntworten } from './Bewegungsartvorschlaege'

import { ariaSortierung, naechste, sortiere } from './Sortierung'
import type { Schluessel, Sortierwahl } from './Sortierung'
import type { Sprachschluessel } from './sprache'
import { Zustand } from './Zustand'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import {
  Zuordnungsfenster,
  offeneBewegungsarten,
  offeneKonten,
  offeneWerte,
} from './Zuordnungsfenster'
import { Sperrgruende, sperrart } from './Sperrgruende'
import { Dateiwahl } from './Dateiwahl'
import { useDarf } from './Rechte'
import { ALLE, Importordnerbaum, Verschiebeleiste, gesellschaftenMitImporten, passtZurWahl } from './Importordner'
import { meldeErfolg } from './Erfolgsstelle'
import { belegeZiehen } from './Ordnerbaum'
import { belegsprung, listenstandLesen } from './Belegsprung'

/** Der Sprung ab Werk: an die Adresse (R156). */
function springe(adresse: string): void {
  window.location.hash = adresse
}
import type { Ordnervorrat, Ordnerwahl } from './Importordner'
import type {
  Datenimportloeschvorschau,
  Datenimportstand,
  Datenquelle,
  Importlaufkopf,
  Importvorschau,
  Katalogeintrag,
  Packagevorschau,
  Quellperiode,
  Stapelvorschau,
} from './typen'


/**
 * Die Lieferung (5.1, R296–R301, R521).
 *
 * **Die Lieferung ist der Vorgang:** eine Datei, geprüft, gezeigt, dann erst
 * übernommen. Das Verzeichnis der Quellen steht seit dem 2026-09-13 in
 * `Datenquelle.tsx` — die Quelle ist Einrichtung, die Lieferung Tagesarbeit.
 *
 * **Was hier ausdrücklich nicht passiert:** Es wird nichts gerechnet und
 * nichts geraten. Die Vorschau zeigt, was in der Datei steht und was daraus
 * würde; übernommen wird genau das, was auf dem Schirm stand.
 */

const ARTEN: { wert: Datenquelle['art']; schluessel: Sprachschluessel }[] = [
  { wert: 'xlsx', schluessel: 'import.art.xlsx' },
  { wert: 'csv', schluessel: 'import.art.csv' },
  { wert: 'sql', schluessel: 'import.art.sql' },
  { wert: 'lucanet', schluessel: 'import.art.lucanet' },
]

/**
 * Die Gesellschaften für die Auswahllisten.
 *
 * Sie stehen im Stammdatenkatalog und nicht in der Kontextauswahl: Die
 * Kontextzeile fragt nach Kreis und Bewertungsebene, eine Lieferung nach der
 * Gesellschaft, die sie geschickt hat.
 */
function useEinheiten(): Katalogeintrag[] {
  const [einheiten, setEinheiten] = useState<Katalogeintrag[]>([])

  useEffect(() => {
    api.stammdatenkatalog().then((k) => setEinheiten(k.einheiten))
  }, [])

  return einheiten
}

function artname(art: string): string {
  const treffer = ARTEN.find((a) => a.wert === art)
  return treffer ? t(treffer.schluessel) : art
}

/* ======================================================================
   Die Lieferung: prüfen, zeigen, übernehmen
   ====================================================================== */

/**
 * Die Periode, mit der ein Import aufschlägt — **der laufende Monat**.
 *
 * **Bis zum 2026-09-18 war es die Berichtsperiode der Kontextzeile** (R330).
 * Die gibt es hier nicht mehr, und sie war auch die falsche Antwort: Wer eine
 * Datei importiert, liefert für den Monat, den die Datei trägt, und nicht für
 * den, den er zuletzt in einem Bericht angesehen hat.
 */
function laufenderMonat(): number {
  const jetzt = new Date()
  return jetzt.getFullYear() * 100 + jetzt.getMonth() + 1
}

/**
 * Der Bereich *Datenimporte* — **die Liste ist der Einstieg** (R633, R608).
 *
 * **Bis zum 2026-09-20 begann dieser Bildschirm mit einem Formular**, und die
 * Läufe standen darunter in einem Aufklappblock als reines Nachschlagewerk.
 * Die erste Frage des Tages lautet aber nicht „welche Datei wähle ich", sondern
 * „was steht schon da und was fehlt". Das Formular ist deshalb der **zweite**
 * Schritt: *Neuer Datenimport* oben, *Aktualisieren* an der Zeile.
 *
 * **Und ein Datenimport ist nicht unwiderruflich** (5.1 Abschnitt 3.17): Er
 * wird gelöscht oder ersetzt, solange die Periode offen ist — Nachbuchungen
 * kommen hoch, und der nächtliche Lauf liefert ohnehin jede Nacht neu.
 */
export function Lieferung({
  aktualisieren = null,
  liste = null,
  aufSprung = springe,
}: {
  /**
   * Der Datenimport, dessen Maske *Aktualisieren* sofort aufgeht — der
   * Verweis aus der Warnung im Konzernabschluss-Monitor (R880). Danach verschwindet er aus der
   * Adresse, sonst ginge die Maske bei jedem Neuladen wieder auf.
   */
  aktualisieren?: number | null
  /**
   * Wie die Liste aussah, bevor zum Beleg gesprungen wurde (5.1 Abschnitt
   * 3.61, R1246) — der Rückweg aus dem Buchungsjournal bringt ihn mit.
   */
  liste?: string | null
  /**
   * Wohin ein Griff springt, der die Seite verlässt (R1245) — ab Werk die
   * Adresse. Als Rückruf, damit die Prüfung der Bedienelemente die Wirkung
   * sieht (R451).
   */
  aufSprung?: (adresse: string) => void
} = {}) {
  const mitgebracht = listenstandLesen(liste ?? undefined)

  // Angekommen: Die Adresse vergisst den Stand wieder, wie bei *Aktualisieren*.
  useEffect(() => {
    if (liste) {
      window.location.hash = '#/datenimport/lieferungen'
    }
  }, [liste])

  const [ansicht, setAnsicht] = useState<'liste' | 'neu' | 'alle'>('liste')
  const [vorbelegung, setVorbelegung] = useState<Vorbelegung | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [stand, setStand] = useState(0)
  // Der links gewählte Ordner überdauert den Gang in die Maske (R970): Wer
  // aus „Abgrenzungen" heraus aktualisiert und zurückgeht, kommt nach
  // „Abgrenzungen" zurück. Nach dem Speichern zeigt er den neuen Eintrag (R1137).
  const [ordnerwahl, setOrdnerwahl] = useState<Ordnerwahl>(mitgebracht?.ordnerwahl ?? ALLE)
  // **Der Filter gilt, bis er geändert oder die Seite verlassen wird** (7.2
  // Abschnitt 35, R1139) — also auch über den Gang in die Maske hinweg. Er
  // steht deshalb hier und nicht in der Liste, die dabei abgebaut wird.
  const [eingrenzung, setEingrenzung] = useState<Eingrenzung>(mitgebracht?.eingrenzung ?? OHNE_EINGRENZUNG)
  /** Was eben gespeichert wurde und in der Liste vor Augen stehen soll (R1136). */
  const [neuheit, setNeuheit] = useState<Neuheit | null>(null)

  useEffect(() => {
    if (!aktualisieren) {
      return
    }

    let gilt = true
    api.datenimporte().then((staende) => {
      const s = staende.find((x) => x.belegId === aktualisieren)
      if (!gilt || !s) {
        return
      }

      setVorbelegung(vorbelegungVon(s))
      setMeldung(null)
      setAnsicht('neu')
      window.location.hash = '#/datenimport/lieferungen'
    })
    return () => {
      gilt = false
    }
  }, [aktualisieren])

  /**
   * Zurück in die Liste, nachdem etwas gespeichert wurde.
   *
   * **Das Kästchen sagt, dass es gelang** (7.2 Abschnitt 34, R1130, R1131),
   * **die Liste zeigt, was entstand** (Abschnitt 35, R1136, R1137). Eine
   * Aufforderung, die an der Speicherung hängt, steht im Kästchen mit einem
   * Knopf, der die Liste auf ihren Ort eingrenzt (R1132).
   */
  function zurListe(erfolgssatz: string, neu: Neuheit, aufforderungen: Folgeaufforderung[] = []) {
    meldeErfolg(
      erfolgssatz,
      aufforderungen.map((a) => ({
        satz: a.satz,
        knopf: t('erfolg.aufforderung.zeigen'),
        aufKnopf: () => setNeuheit({ laeufe: [], einheit: a.einheit, perioden: [a.periode] }),
      })),
    )
    setMeldung(null)
    setVorbelegung(null)
    setNeuheit(neu)
    setAnsicht('liste')
    setStand((n) => n + 1)
  }

  if (ansicht === 'neu') {
    return (
      <Neuerimport
        vorbelegung={vorbelegung}
        aufFertig={zurListe}
        aufZurueck={() => {
          setVorbelegung(null)
          setAnsicht('liste')
        }}
      />
    )
  }

  // **Alle angemahnten in einem Zug** (R932) — ein eigener Schritt wie die
  // Maske: erst zeigen, was geschieht, dann buchen.
  if (ansicht === 'alle') {
    return <Sammelaktualisierung aufFertig={zurListe} aufZurueck={() => setAnsicht('liste')} />
  }

  return (
    <Datenimporte
      stand={stand}
      ordnerwahl={ordnerwahl}
      aufOrdnerwahl={setOrdnerwahl}
      eingrenzung={eingrenzung}
      aufEingrenzung={setEingrenzung}
      aufSprung={aufSprung}
      neuheit={neuheit}
      meldung={meldung}
      aufMeldung={setMeldung}
      aufAlle={() => {
        setMeldung(null)
        setAnsicht('alle')
      }}
      aufNeu={(v) => {
        setVorbelegung(v)
        setMeldung(null)
        setAnsicht('neu')
      }}
      aufGeaendert={() => setStand((n) => n + 1)}
    />
  )
}

/**
 * Was die Maske vorbelegt bekommt, wenn jemand an einer Zeile *Aktualisieren*
 * drückt.
 *
 * **Die drei Angaben stehen in der Zeile, auf die er gezeigt hat**, und werden
 * kein zweites Mal gefragt. Die Bewertungsebene ist nicht dabei: Sie sagt die
 * Quelle (R537) und steht hier nicht zur Wahl.
 */
type Vorbelegung = {
  einheit: string
  periode: number
  quelle: string | null
  /**
   * Der Datenimport, der ersetzt wird (R639) — die Kennung seines Belegs.
   *
   * **Sie ist das ganze Verfahren.** Bis zum 2026-09-20 suchte der Rechenkern
   * selbst nach dem Vorgänger und traf dabei jeden Lieferbeleg der Klammer,
   * auch eine Nachbuchung des Vormonats. Jetzt zeigt jemand darauf.
   */
  ersetzt: number
  /** Worunter er läuft (R374) — für den Satz über der Maske und als Vorbelegung des Namensfeldes. */
  name: string
  zeilen: number
  /**
   * Ob die Maske die aufbewahrte Datei einlegt (R931). Eine Nachbuchung und
   * ein Datenimport ohne Datenquelle bleiben, wie sie waren; ob wirklich eine
   * Datei liegt, sagt der Rechenkern (R933).
   */
  ausAufbewahrung: boolean
}

/**
 * Was eben gespeichert wurde (7.2 Abschnitt 35, R1136).
 *
 * **Erkannt wird es an seinen Läufen** — der Datenimport, das Package und der
 * Stapel bringen je Monat einen mit. Gesellschaft und Perioden ergeben sich
 * dann aus den Zeilen selbst; so steht auch eine Nachbuchung richtig, deren
 * Zeile eine andere Periode trägt als die gelieferte. Ohne Lauf — der Knopf
 * zur Folgeperiode — sagen `einheit` und `perioden`, wohin die Liste schaut.
 */
export type Neuheit = {
  laeufe: number[]
  einheit?: string
  perioden?: number[]
}

/** Eine Aufforderung, die an der Speicherung hängt (R1132) — und der Ort, an den ihr Knopf führt. */
export type Folgeaufforderung = { satz: string; einheit: string; periode: number }

/**
 * Was im Feld Periode steht, wenn die Liste auf das Neue schaut (R1138).
 *
 * **Ein Monat steht als Monat, mehrere eines Jahres als das Jahr.** Das Feld
 * sucht „enthält", und jede Schreibweise einer Periode enthält ihr Jahr — eine
 * neue Schreibweise braucht es dafür nicht. **Über den Jahreswechsel bleibt es
 * leer** (die Auflage): *2026* träfe von November 2025 bis Februar 2026 nur
 * die Hälfte.
 */
export function periodeneingabe(perioden: number[]): string {
  const verschieden = [...new Set(perioden)]
  if (verschieden.length === 0) {
    return ''
  }
  if (verschieden.length === 1) {
    return periodenname(verschieden[0]!)
  }
  const jahre = new Set(verschieden.map((p) => Math.floor(p / 100)))
  return jahre.size === 1 ? String([...jahre][0]) : ''
}

/** Die Vorbelegung aus der Zeile, auf die jemand zeigt — oder aus dem Verweis im Konzernabschluss-Monitor (R880). */
function vorbelegungVon(s: Datenimportstand): Vorbelegung {
  return {
    einheit: s.einheit,
    periode: s.periode,
    quelle: s.datenquelle,
    ersetzt: s.belegId,
    name: s.name,
    zeilen: s.zeilen,
    ausAufbewahrung: s.nachbuchungFuer == null && s.datenquelle !== null,
  }
}

/**
 * Die Gesellschaften, die in einem Datenimport stehen.
 *
 * **Heute ist es genau eine**, und die Liste steht trotzdem hier: Eine
 * Lieferung kann mehrere Gesellschaften tragen. Wenn sie es tut, filtert
 * dieselbe Stelle über alle — nicht über die erste, die zufällig oben stand.
 */
function gesellschaften(s: Datenimportstand): string[] {
  return [s.einheit]
}

/**
 * Die Schreibweisen einer Periode, gegen die „enthält" prüft.
 *
 * Wer `09.2026` tippt, meint dieselbe Periode wie der, der *September*
 * schreibt oder `202609` aus einem Protokoll kopiert. Alle drei stehen hier,
 * damit keine davon ins Leere läuft.
 *
 * **Dazu die Schreibweisen, die das Periodenfeld annimmt** (7.2 Abschnitt 21,
 * R839 — Entscheidung 2 vom 2026-09-30): `09/2026`, `09-2026` und `2026-09`.
 * Was sich in ein Feld tippen lässt, findet auch das Filterfeld.
 */
function periodenschreibweisen(periode: number): string[] {
  const monat = String(periode % 100).padStart(2, '0')
  const jahr = String(Math.floor(periode / 100))
  return [
    String(periode),
    periodenname(periode),
    `${monat}.${jahr}`,
    `${monat}/${jahr}`,
    `${monat}-${jahr}`,
    `${jahr}-${monat}`,
  ]
}

/** Was in den drei Filterfeldern steht. Leer heißt: grenzt nichts ein. */
type Eingrenzung = { periode: string; einheit: string; schicht: string }

const OHNE_EINGRENZUNG: Eingrenzung = { periode: '', einheit: '', schicht: '' }

/**
 * „Enthält", ohne Rücksicht auf Groß- und Kleinschreibung.
 *
 * **Kein „beginnt mit" und kein Sternchen.** Wer `FR` tippt, sucht die
 * französische Gesellschaft und weiß nicht, ob ihr Code mit `EH-` anfängt.
 */
function enthaelt(vorrat: string[], gesucht: string): boolean {
  const nadel = gesucht.trim().toLocaleLowerCase()
  return nadel === '' || vorrat.some((h) => h.toLocaleLowerCase().includes(nadel))
}

/* ----------------------------------------------------------------------
   Sortieren nach Spalte (5.1 Abschnitt 3.17)
   ---------------------------------------------------------------------- */

/**
 * Die Spalten der Ständeliste — Kopf, Sortierschlüssel und Filterfeld an einem
 * Ort.
 *
 * **Eine Liste und nicht sechsmal dasselbe im Markup**: Der Kopf, die
 * `aria-sort`-Angabe, der Klick und das Filterzeichen hängen alle an derselben
 * Kennung. Getrennt aufgeschrieben liefe eines davon irgendwann der Reihenfolge
 * hinterher.
 */
const SPALTEN: { id: string; kopf: Sprachschluessel; filter?: keyof Eingrenzung }[] = [
  { id: 'name', kopf: 'import.spalte.belegname' },
  { id: 'einheit', kopf: 'import.spalte.einheit', filter: 'einheit' },
  { id: 'ledger', kopf: 'import.spalte.schicht', filter: 'schicht' },
  { id: 'periode', kopf: 'import.spalte.periode', filter: 'periode' },
  // „Anwender", wie in jedem Protokoll (7.2 Abschnitt 42, R1271) — hier stand „ausgelöst von".
  { id: 'ausgeloest', kopf: 'protokoll.spalte.anwender' },
  { id: 'zustand', kopf: 'import.spalte.zustand' },
]

/**
 * Wonach sortiert wird, solange niemand geklickt hat: **Periode, neueste oben.**
 *
 * Das ist zugleich die Ordnung, in der der Rechenkern liefert
 * (`ORDER BY periode DESC`) — die Vorgabe und „die Reihenfolge der Liste
 * selbst" sind hier dieselbe Sache. Deshalb führt der dritte Klick auf eine
 * Spalte hierher zurück und nicht in einen dritten, unsichtbaren Zustand.
 */
const VORGABE: { spalte: string; richtung: 'auf' | 'ab' } = { spalte: 'periode', richtung: 'ab' }

/**
 * Der nächste Zustand nach einem Klick — die Hausregel aus `Sortierung.ts`,
 * mit einer Ausnahme, die sie nicht bricht.
 *
 * **Auf der Vorgabespalte gibt es nur zwei Zustände.** Die dritte Stufe der
 * Hausregel heißt „zurück zur Reihenfolge der Liste", und die *ist* hier die
 * Vorgabe. Ohne die Ausnahme wäre jeder dritte Klick auf *Periode* ein Klick,
 * der nichts tut — und der sieht aus wie ein kaputter Knopf.
 */
function naechsteWahl(jetzt: Sortierwahl, spalte: string): Sortierwahl {
  const wirksam = jetzt ?? VORGABE

  if (spalte === VORGABE.spalte && wirksam.spalte === spalte) {
    return { spalte, richtung: wirksam.richtung === 'ab' ? 'auf' : 'ab' }
  }

  return naechste(wirksam, spalte) ?? VORGABE
}

/**
 * Der Zustand sortiert nach Dringlichkeit, nicht nach Alphabet.
 *
 * Wer nach dieser Spalte sortiert, sucht das, was ihn aufhält — nicht den
 * Buchstaben g. Die Reihenfolge ist dieselbe, in der `Standzustand` entscheidet,
 * was es zeigt: Zeilen mit demselben Zeichen stehen dann auch beieinander.
 */
function zustandsrang(s: Datenimportstand): number {
  if (s.geschlossenDurch) return 0
  if (s.nachbuchungFuer !== null) return 1
  if (s.freigegeben.length > 0) return 2
  return 3
}

/**
 * Was eine Spalte zum Vergleichen hergibt.
 *
 * **Sortiert wird nach dem, was in der Zelle steht** (R838, Befund vom
 * 2026-09-29): nach dem Namen der Gesellschaft, nicht nach ihrem Schlüssel.
 * Sonst stünde die Holding zwischen zwei Töchtern, weil ihre Kennung
 * zufällig dazwischen liegt.
 *
 * **Mit einer Ausnahme: Die Periode im Belegnamen ordnet in ihrer
 * gespeicherten Schreibweise** (7.2 Abschnitt 21, R839). Die Zelle liest
 * „Saldenliste Holding AG Januar 2026"; nach diesem Text sortiert stünde
 * der April vor dem Januar. Deshalb hier `mitNamen` und nicht `belegtext`:
 * die Gesellschaft beim Namen, die Periode so, wie der Beleg sie trägt.
 */
function sortschluessel(s: Datenimportstand, spalte: string): Schluessel {
  switch (spalte) {
    case 'name':
      return mitNamen(s.name)
    case 'einheit':
      return gesellschaften(s).map(gesellschaftsname).join(', ')
    case 'ledger':
      return bewertungsebenenname(s.ledger)
    case 'periode':
      return s.periode
    case 'ausgeloest':
      return anwendername(s.erfasstVon)
    case 'zustand':
      return zustandsrang(s)
    default:
      return null
  }
}

/**
 * Die drei Felder gelten zusammen — jedes grenzt weiter ein, keines hebt auf.
 *
 * **Eingegrenzt wird am Namen** (R838): Wer *Holding* tippt, findet die
 * Holding AG. Der Schlüssel bleibt daneben als zweite Schreibweise — wie die
 * Periode ihre drei hat —, damit auch trifft, wer ihn aus einem Protokoll
 * kopiert.
 */
function passt(s: Datenimportstand, e: Eingrenzung): boolean {
  return (
    enthaelt(periodenschreibweisen(s.periode), e.periode) &&
    enthaelt(gesellschaften(s).flatMap((g) => [gesellschaftsname(g), g]), e.einheit) &&
    enthaelt([bewertungsebenenname(s.ledger), s.ledger], e.schicht)
  )
}

/** Ein Filterfeld — drei Mal dieselbe Form, damit keines aus der Reihe fällt. */
function Filterfeld({
  name,
  wert,
  aufWert,
}: {
  name: string
  wert: string
  aufWert: (text: string) => void
}) {
  return (
    <label>
      <span className="name">{name}</span>
      <input type="text" value={wert} onChange={(e) => aufWert(e.target.value)} />
    </label>
  )
}

/* ======================================================================
   Die gespeicherten Datenimporte — was gilt, nicht was geschah (R633)
   ====================================================================== */

function Datenimporte({
  stand,
  ordnerwahl,
  aufOrdnerwahl,
  eingrenzung,
  aufEingrenzung,
  aufSprung,
  neuheit,
  meldung,
  aufMeldung,
  aufNeu,
  aufAlle,
  aufGeaendert,
}: {
  stand: number
  /** Der links gewählte Ordner (R970) — er gehört der Seite, nicht der Liste. */
  ordnerwahl: Ordnerwahl
  aufOrdnerwahl: (wahl: Ordnerwahl) => void
  /** Was in den drei Filterfeldern steht — ebenso der Seite (R1139). */
  eingrenzung: Eingrenzung
  aufEingrenzung: (e: Eingrenzung | ((bisher: Eingrenzung) => Eingrenzung)) => void
  /** Ein Griff verlässt die Seite — zum Beleg im Buchungsjournal (R1245). */
  aufSprung: (adresse: string) => void
  /** Was eben gespeichert wurde und vor Augen stehen soll (R1136). */
  neuheit: Neuheit | null
  meldung: string | null
  aufMeldung: (text: string | null) => void
  aufNeu: (vorbelegung: Vorbelegung | null) => void
  /** Alle angemahnten in einem Zug (R932). */
  aufAlle: () => void
  aufGeaendert: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('datenimport:datenimporte')

  const darf = useDarf()

  /*
    **Die Liste kommt ungefiltert.** Bis zum 2026-09-20 stand oben ein
    Periodenfeld, vorbelegt auf den laufenden Monat — wer den Datenimport vom
    Vormonat suchte, sah zuerst eine leere Tabelle und musste das Feld erst
    leeren, um überhaupt zu erfahren, dass es ihn gibt. Jetzt steht der ganze
    Bestand da, und eingegrenzt wird an dem, was schon auf dem Schirm ist.
  */
  const [staende, setStaende] = useState<Datenimportstand[]>([])
  const [laedt, setLaedt] = useState(true)
  const setEingrenzung = aufEingrenzung
  /** `null` heißt: die Vorgabe (Periode, neueste oben) — nicht „unsortiert". */
  const [wahl, setWahl] = useState<Sortierwahl>(null)
  const [loeschen, setLoeschen] = useState<Datenimportloeschvorschau | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [historie, setHistorie] = useState<number | null>(null)
  const [laeufe, setLaeufe] = useState<Importlaufkopf[]>([])

  /*
    **Links der Ordnerbaum des Buchungsjournals** (5.1 Abschnitt 3.45,
    R970): Ein Datenimport ist ein Beleg und liegt dort, wo das Journal ihn
    zeigt. Rechts wird markiert und verschoben.
  */
  const [vorrat, setVorrat] = useState<Ordnervorrat>({})
  const [markiert, setMarkiert] = useState<Set<number>>(new Set())
  const [verschoben, setVerschoben] = useState(0)

  useEffect(() => {
    let gilt = true
    setLaedt(true)
    api.datenimporte(null).then((d) => {
      if (gilt) {
        setStaende(d)
        setLaedt(false)
      }
    })
    return () => {
      gilt = false
    }
  }, [stand, verschoben])

  /*
    **Die Ordner der Gesellschaften, die Datenimporte tragen** — je
    Gesellschaft ein Ruf, derselbe wie im Buchungsjournal. Bleibt einer aus,
    steht die Gesellschaft ohne Ordner da; die Liste daneben gilt trotzdem.
  */
  const einheitenschluessel = gesellschaftenMitImporten(staende).join('|')
  useEffect(() => {
    let gilt = true
    const einheiten = einheitenschluessel === '' ? [] : einheitenschluessel.split('|')
    Promise.all(
      einheiten.map((e) =>
        Promise.resolve()
          // Ein Nachschlagen im Hintergrund (R1007): still, mit einem zweiten Versuch.
          .then(() => imHintergrund(() => api.belegordner(e)))
          .then((o) => [e, o ?? []] as const)
          .catch(() => [e, []] as const),
      ),
    ).then((paare) => {
      if (gilt) {
        setVorrat(Object.fromEntries(paare))
      }
    })
    return () => {
      gilt = false
    }
  }, [einheitenschluessel, verschoben])

  /*
    **Gefiltert wird hier und nicht am Server.** Die Zeilen liegen schon im
    Browser, und ein „enthält" über drei Felder ist eine Schleife — ein Ruf
    dafür wäre ein Ruf, der die Tabelle bei jedem Tastendruck neu aufbaut.
    Der gewählte Ordner grenzt ebenso ein wie die drei Felder.
  */
  const gefiltert = useMemo(
    () => staende.filter((s) => passt(s, eingrenzung) && passtZurWahl(s, ordnerwahl, vorrat)),
    [staende, eingrenzung, ordnerwahl, vorrat],
  )

  /*
    **Erst eingrenzen, dann sortieren** — nicht umgekehrt. Sortieren kostet
    n·log n über den ganzen Bestand; die Eingrenzung nimmt oft neun Zehntel weg,
    und was weg ist, muss nicht verglichen werden.
  */
  const wirksam = wahl ?? VORGABE
  const gezeigt = useMemo(
    () => sortiere(gefiltert, wirksam, sortschluessel),
    [gefiltert, wirksam],
  )

  /*
    **Nach dem Speichern steht das Neue vor Augen** (7.2 Abschnitt 35,
    R1136–R1138): links die Gesellschaft, in den Feldern Gesellschaft und
    Periode, die neuen Zeilen hinterlegt und ins Bild gerollt. Einmal je
    Speicherung — danach gehören Ordner und Felder wieder dem Anwender.
  */
  const [hervorgehoben, setHervorgehoben] = useState<Set<number>>(new Set())
  const angewandt = useRef<Neuheit | null>(null)
  useEffect(() => {
    if (!neuheit || laedt || angewandt.current === neuheit) {
      return
    }
    angewandt.current = neuheit

    const neue = staende.filter((s) => s.importlaufId !== null && neuheit.laeufe.includes(s.importlaufId))
    const einheiten = [
      ...new Set(neue.length > 0 ? neue.map((s) => s.einheit) : neuheit.einheit ? [neuheit.einheit] : []),
    ]
    const perioden = neue.length > 0 ? neue.map((s) => s.periode) : (neuheit.perioden ?? [])
    const einheit = einheiten.length === 1 ? einheiten[0]! : null

    aufOrdnerwahl(einheit ? { art: 'gesellschaft', einheit } : ALLE)
    setEingrenzung({
      periode: periodeneingabe(perioden),
      einheit: einheit ? gesellschaftsname(einheit) : '',
      schicht: '',
    })
    setHervorgehoben(new Set(neue.map((s) => s.belegId)))
  }, [neuheit, laedt, staende, aufOrdnerwahl, setEingrenzung])

  const tabelle = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (hervorgehoben.size > 0) {
      tabelle.current?.querySelector('tr.neu')?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [hervorgehoben])

  /** Wie viele Datenimporte eine Aktualisierung brauchen (R880) — im ganzen Bestand, nicht im Ausschnitt. */
  const angemahnt = useMemo(() => staende.filter((s) => s.aktualisierungNach != null).length, [staende])

  const eingegrenzt =
    eingrenzung.periode.trim() !== '' ||
    eingrenzung.einheit.trim() !== '' ||
    eingrenzung.schicht.trim() !== ''

  /** Was markiert ist und noch dasteht — ein verschwundener Datenimport ist nicht mehr markiert. */
  const markierte = staende.filter((s) => markiert.has(s.belegId))
  const alleGezeigtenMarkiert = gezeigt.length > 0 && gezeigt.every((s) => markiert.has(s.belegId))

  function markiere(belegId: number, an: boolean) {
    setMarkiert((m) => {
      const neu = new Set(m)
      if (an) {
        neu.add(belegId)
      } else {
        neu.delete(belegId)
      }
      return neu
    })
  }

  /**
   * Die markierten Datenimporte in einen Ordner legen (R970).
   *
   * **Fasst keinen Beleg an** (R375) — derselbe Weg wie Strg+X, Strg+V im
   * Buchungsjournal, je Datenimport ein Ruf.
   */
  async function verschiebenTun(ziel: number) {
    setLaeuft(true)
    try {
      for (const s of markierte) {
        await api.belegAblegen(s.belegId, ziel)
      }
      // Die Tat und ihr Gegenstand, keine Zahl (7.2 Abschnitt 34, R1131).
      meldeErfolg(
        t(markierte.length === 1 ? 'erfolg.datenimport.verschoben' : 'erfolg.datenimporte.verschoben'),
      )
      aufMeldung(null)
      setMarkiert(new Set())
    } catch (f) {
      // Nur ein fachlicher Satz steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      aufMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
      setVerschoben((v) => v + 1)
    }
  }

  async function historieZeigen(s: Datenimportstand) {
    if (historie === s.belegId) {
      setHistorie(null)
      return
    }

    setHistorie(s.belegId)
    setLaeufe(await api.verlaufZuBeleg(s.belegId))
  }

  async function loeschenTun() {
    if (!loeschen) {
      return
    }

    setLaeuft(true)
    try {
      await api.datenimportLoeschen(loeschen.stand.belegId)
      meldeErfolg(t('erfolg.datenimport.geloescht'))
      aufMeldung(null)
      setLoeschen(null)
      aufGeaendert()
    } catch (f) {
      aufMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <section>
      <h1>{t('bereich.datenimport.lieferungen')}</h1>

      {/*
        **Oben steht, was hier geht** (R145) — und der erste Weg davon steht
        daneben. Der Knopf gehört nicht in den Kopf der Liste: Ein neuer
        Datenimport grenzt den Bestand nicht ein, er kommt hinzu.
      */}
      <div className="bereichseinstieg">
        <p className="erlaeuterung">{t('import.bereich.hinweis')}</p>

        {/* Die Maske dient dem Übernehmen; wer das nicht darf, bekommt den Weg nicht (R1180). */}
        {darf('importUebernehmen') && (
          <button type="button" className="haupt" onClick={() => aufNeu(null)}>
            {t('import.neu')}
          </button>
        )}

        {/*
          **Nur, solange einer angemahnt ist** (R932): Ein Knopf, der nichts
          zu tun hat, stünde nur da.
        */}
        {darf('aktualisierungsvorschau') && angemahnt > 0 && (
          <button type="button" onClick={aufAlle}>
            {t('import.aktualisierung.alle', { anzahl: anzahlwort(angemahnt) })}
          </button>
        )}
      </div>

      {meldung && <p className="hinweis">{meldung}</p>}

      {/*
        **Links der Baum, rechts die Liste** (5.1 Abschnitt 3.45, R970) — das
        Brett des Buchungsjournals, mit denselben Ordnern.
      */}
      <div className="ordnerbrett importordner">
        <Importordnerbaum
          staende={staende}
          vorrat={vorrat}
          wahl={ordnerwahl}
          aufWahl={aufOrdnerwahl}
          aufGeaendert={() => setVerschoben((v) => v + 1)}
          aufBelegeGezogen={async (ids, ziel) => {
            // Ziehen verschiebt (R983) — derselbe Weg wie „Verschieben nach".
            for (const id of ids) {
              await api.belegAblegen(id, ziel)
            }
            setMarkiert(new Set())
          }}
        />

        <div className="ordnerinhalt">
          {darf('belegAblegen') && markierte.length > 0 && (
            <Verschiebeleiste
              markiert={markierte}
              vorrat={vorrat}
              laeuft={laeuft}
              aufVerschieben={(ziel) => void verschiebenTun(ziel)}
              aufAufheben={() => setMarkiert(new Set())}
            />
          )}

          {/*
            **Der Kopf des zweiten Blocks sagt, wonach er eingrenzt** — und die
            Felder, die es tun, stehen unter dem Satz und nicht irgendwo darüber.
            Die Liste darunter ist der ganze Bestand, nicht ein Ausschnitt, den
            jemand beim Öffnen gesetzt hat.
          */}
          <div className="filterkopf">
            <p className="erlaeuterung">{t('import.filter.kopf')}</p>

            <div className="feldzeile">
              <Filterfeld
                name={t('import.spalte.periode')}
                wert={eingrenzung.periode}
                aufWert={(periode) => setEingrenzung((e) => ({ ...e, periode }))}
              />
              <Filterfeld
                name={t('import.spalte.einheit')}
                wert={eingrenzung.einheit}
                aufWert={(einheit) => setEingrenzung((e) => ({ ...e, einheit }))}
              />
              <Filterfeld
                name={t('import.spalte.schicht')}
                wert={eingrenzung.schicht}
                aufWert={(schicht) => setEingrenzung((e) => ({ ...e, schicht }))}
              />
            </div>
          </div>

          {/*
            **Kein Zähler über der Tabelle** (7.2 Abschnitt 34, R1135 —
            Entscheidung vom 2026-10-06). Bis dahin stand hier „2 von 76
            gespeicherten Datenimporten". Welche Spalte eingrenzt, sagt das
            Zeichen im Kopf; die Zahlen stehen an den Ordnern links.

            **Die Markierung des Neuen geht mit dem ersten Klick in die Liste**
            (R1136).
          */}
          <div
            ref={tabelle}
            className="tabellenrahmen"
            onPointerDown={() => hervorgehoben.size > 0 && setHervorgehoben(new Set())}
          >
            <table ref={spaltenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse} />
              <thead>
                {/*
                  **Der Name steht vorn** (R374): Er ist das, wonach ein Anwender
                  sucht. Die Erläuterung, die bis zum 2026-09-20 hier stand, ist ein
                  maschineller Satz über den Inhalt und kein Name — sie bleibt im
                  Journal und im Wertenachweis, wo sie hingehört.

                  **Und jeder Kopf sortiert** — mit demselben Bauteil und derselben
                  Regel wie die Journalsuche und der Belegordner. Eine dritte
                  Tabelle, die sich beim zweiten Klick anders verhält, wäre
                  schlimmer als eine, die gar nicht sortiert.
                */}
                <tr>
                  {/* Markiert wird zum Verschieben — ohne das Recht fehlt die Spalte (R1180). */}
                  {darf('belegAblegen') && (
                    <th className="markierspalte">
                      <input
                        type="checkbox"
                        aria-label={t('import.ordner.allemarkieren')}
                        title={t('import.ordner.allemarkieren')}
                        checked={alleGezeigtenMarkiert}
                        disabled={gezeigt.length === 0}
                        onChange={(e) => {
                          const an = e.target.checked
                          setMarkiert((m) => {
                            const neu = new Set(m)
                            for (const s of gezeigt) {
                              if (an) neu.add(s.belegId)
                              else neu.delete(s.belegId)
                            }
                            return neu
                          })
                        }}
                      />
                    </th>
                  )}
                  {SPALTEN.map((sp) => (
                    <th key={sp.id} aria-sort={ariaSortierung(wirksam, sp.id)}>
                      <Spaltenkopf
                        kopf={t(sp.kopf)}
                        wahl={wirksam}
                        spalte={sp.id}
                        aufKlick={() => setWahl((v) => naechsteWahl(v, sp.id))}
                      />

                      {/*
                        **Das Zeichen sagt, an welcher Spalte die Eingrenzung
                        hängt** (R145) — seit R1135 die einzige Spur davon über
                        der Liste.
                      */}
                      {sp.filter && eingrenzung[sp.filter].trim() !== '' && (
                        <span
                          className="filterzeichen"
                          role="img"
                          title={t('import.filter.aktiv')}
                          aria-label={t('import.filter.aktiv')}
                        >
                          ●
                        </span>
                      )}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {gezeigt.map((s) => (
                  <Fragment key={s.belegId}>
                    <tr
                      className={hervorgehoben.has(s.belegId) ? 'neu' : undefined}
                      /*
                        **Ziehen verschiebt** (R983): eine Zeile auf einen
                        Ordner links — oder alle markierten derselben
                        Gesellschaft, wenn die gezogene darunter ist.
                      */
                      draggable={darf('belegAblegen')}
                      onDragStart={(e) =>
                        belegeZiehen(
                          e,
                          s.einheit,
                          markiert.has(s.belegId)
                            ? markierte
                                .filter((m) => m.einheit === s.einheit)
                                .map((m) => ({ id: m.belegId, periode: m.periode }))
                            : [{ id: s.belegId, periode: s.periode }],
                        )
                      }
                    >
                      {/*
                        **Die Zelle trägt die Bezeichnung; die Kennung steht nur im
                        Blatt** (7.2 Abschnitt 20, R838 — Befund vom 2026-09-29):
                        Hier stand „EH-000005-57" für die Holding AG, „LDG-ROH"
                        für die Rohdaten und „system" als Auslöser. Der Belegname
                        ist ein gespeicherter Text und bleibt, wie er ist — die
                        Anzeige liest ihn mit Namen.

                        **Und mit Monat und Jahr** (7.2 Abschnitt 21, R839 —
                        Entscheidung 3 vom 2026-09-30): aus „… 202612" und
                        „… 12/2026" wird „… Dezember 2026".
                      */}
                      {darf('belegAblegen') && (
                        <td className="markierspalte">
                          <input
                            type="checkbox"
                            aria-label={`${t('import.ordner.markieren')}: ${belegtext(s.name)}`}
                            checked={markiert.has(s.belegId)}
                            onChange={(e) => markiere(s.belegId, e.target.checked)}
                          />
                        </td>
                      )}
                      <td>{belegtext(s.name)}</td>
                      <td>{gesellschaften(s).map(gesellschaftsname).join(', ')}</td>
                      <td>{bewertungsebenenname(s.ledger)}</td>
                      <td>{periodenname(s.periode)}</td>
                      <td>
                        <span>{anwendername(s.erfasstVon)}</span>{' '}
                        {/* Mit Uhrzeit: Am Abschlusstag wird mehrfach aktualisiert (R979). */}
                        <span className="gedaempft">
                          {zeitpunkt(s.erfasstAm)}
                        </span>
                      </td>
                      <td>
                        <Standzustand stand={s} />
                      </td>
                      <td className="zeilenhandgriffe">
                        {/*
                          **Aktualisieren ist kein neuer Mechanismus** (R132): Es
                          öffnet die Maske mit dem, was in dieser Zeile steht. Was
                          dann übernommen wird, ersetzt diesen Stand — wie seit je,
                          nur dass man es jetzt sieht.
                        */}
                        {darf('importUebernehmen') && (
                          <button
                            type="button"
                            className="schmal"
                            disabled={!s.istLoeschbar}
                            onClick={() =>
                              aufNeu(vorbelegungVon(s))
                            }
                          >
                            {t('import.stand.aktualisieren')}
                          </button>
                        )}
                        {darf('datenimportLoeschen') && (
                          <button
                            type="button"
                            className="schmal"
                            onClick={async () =>
                              setLoeschen(await api.datenimportLoeschvorschau(s.belegId))
                            }
                          >
                            {t('import.stand.loeschen')}
                          </button>
                        )}
                        {/*
                          **Die Historie steht an der Zeile, zu der sie gehört**
                          (R633). Der Aufklappblock mit den letzten 15 Läufen ist
                          dafür entfallen: Er zeigte Läufe ohne Bezug.
                          **Der Knopf trägt keine Zahl** (7.2 Abschnitt 36.2,
                          berichtigt am 2026-10-06): Hier stand „19 Läufe",
                          fast alle davon ersetzte Testimporte.
                        */}
                        {s.laeufe > 0 && (
                          <button
                            type="button"
                            className="schmal"
                            onClick={() => void historieZeigen(s)}
                          >
                            {t('import.stand.verlauf')}
                          </button>
                        )}
                        {/*
                          **Zum Beleg, den dieser Datenimport gebucht hat**
                          (5.1 Abschnitt 3.61, R1245) — ins Buchungsjournal,
                          dort aufgeschlagen. Die Liste reist in der Adresse
                          mit, damit der Rückweg sie so zeigt, wie sie war (R1246).
                        */}
                        <button
                          type="button"
                          className="schmal"
                          onClick={() =>
                            aufSprung(
                              belegsprung(
                                { belegId: s.belegId, einheit: s.einheit, ordnerId: s.ordner ?? null },
                                { eingrenzung, ordnerwahl },
                              ),
                            )
                          }
                        >
                          {t('import.stand.beleg')}
                        </button>
                      </td>
                    </tr>

                    {historie === s.belegId && (
                      <tr className="aufriss">
                        <td colSpan={8}>
                          <Historie laeufe={laeufe} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}

                {/*
                  **Leer ist nicht gleich leer** (R144): „es gibt keinen" schickt
                  zur Maske, „keiner passt" zu den Feldern darüber, „hier liegt
                  keiner" an den Ordner links.
                */}
                {!laedt && gezeigt.length === 0 && (
                  <tr>
                    <td colSpan={8} className="hinweis">
                      {eingegrenzt
                        ? t('import.staende.leer.eingrenzung')
                        : ordnerwahl.art === 'ordner'
                          ? t('import.ordner.leer')
                          : t('import.staende.leer')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {loeschen && (
        <Loeschabfrage
          vorschau={loeschen}
          laeuft={laeuft}
          aufLoeschen={() => void loeschenTun()}
          aufSchliessen={() => setLoeschen(null)}
        />
      )}
    </section>
  )
}

/**
 * Was an einem Stand hängt — in einem Blick (R635).
 *
 * **Geschlossen ist eine Sperre, freigegeben ein Satz davor.** Beides steht
 * hier, weil beides die nächste Handlung bestimmt: Das eine sagt „geht nicht",
 * das andere „geht, aber jemand hat das schon freigegeben".
 */
function Standzustand({ stand }: { stand: Datenimportstand }) {
  if (stand.geschlossenDurch) {
    return <Zustand art="blockiert" text={t('import.stand.geschlossen', { kreis: kreisname(stand.geschlossenDurch) })} />
  }

  // **Neu abzuleiten** (R880): Die Vorperiode wurde nach diesem Stand
  // übernommen und hat ihn nicht fortgeschrieben. Aktualisieren macht ihn
  // jünger als den Hinweis — dann steht hier wieder „gilt".
  if (stand.aktualisierungNach) {
    return (
      <Zustand
        art="veraltet"
        text={t('import.stand.aktualisierung', { periode: periodenname(stand.aktualisierungNach) })}
      />
    )
  }

  if (stand.nachbuchungFuer) {
    return (
      <Zustand
        art="offen"
        text={t('import.stand.nachbuchung', { periode: periodenname(stand.nachbuchungFuer) })}
      />
    )
  }

  if (stand.freigegeben.length > 0) {
    return <Zustand art="veraltet" text={t('import.stand.freigegeben', { kreise: stand.freigegeben.map(kreisname).join(', ') })} />
  }

  return <Zustand art="fertig" text={t('import.stand.gilt')} />
}

/** Die Läufe hinter einem Stand — aufgeklappt an seiner Zeile (R633). */
function Historie({ laeufe }: { laeufe: Importlaufkopf[] }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('datenimport:historie')

  if (laeufe.length === 0) {
    return <p className="hinweis">{t('import.stand.laeufe.leer')}</p>
  }

  return (
    <table ref={spaltenrahmen} className="innen ziehbaretabelle">
      <Spaltenmassleiste masse={spaltenmasse} />
      <thead>
        {/*
          **Ohne Laufnummer und ohne Zustand** (7.2 Abschnitt 34, R1135,
          berichtigt am 2026-10-06): Hier stand „17 · uebernommen", dann
          „übernommen" in jeder Zeile — ein Lauf entsteht nur bei der
          Übernahme, einen anderen Zustand schreibt die Anwendung nicht.
        */}
        {/* Ohne gelesene und übernommene Zeilen (7.2 Abschnitt 36, R1143). */}
        {/* Mit dem Anwender, wie jedes Protokoll (7.2 Abschnitt 42, R1270). */}
        <tr>
          <th>{t('import.spalte.quelle')}</th>
          <th>{t('protokoll.spalte.anwender')}</th>
          <th>{t('import.spalte.wann')}</th>
        </tr>
      </thead>
      <tbody>
        {laeufe.map((l) => (
          <Fragment key={l.id}>
            <tr>
              {/* Wer geliefert hat, beim Namen — „DQ-000011-38" ist keine Auskunft (R838). */}
              <td>{l.datenquelle ? datenquellenname(l.datenquelle) : '—'}</td>
              <td>{anwendername(l.erfasstVon)}</td>
              <td className="gedaempft">{zeitpunkt(l.erfasstAm)}</td>
            </tr>
            {/*
              **Nur, was bei der Übernahme entstand** (5.1 Abschnitt 3.36, R878):
              die Antwort auf einen Vorschlag, der Hinweis zur Folgeperiode. Was
              schon in der Vorschau stand, wiederholt die Historie nicht — und
              ein Lauf ohne solchen Satz bekommt keine leere Zeile (R145).
            */}
            {(l.uebernahmesaetze ?? []).length > 0 && (
              <tr className="laufsaetze">
                <td colSpan={3}>
                  {l.uebernahmesaetze!.map((s, i) => (
                    <p key={i} className="erlaeuterung">
                      {belegtext(s)}
                    </p>
                  ))}
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Die Warnabfrage vor dem Löschen (R638).
 *
 * **Kein „Wirklich löschen?".** Sie nennt Gesellschaft, Periode, Schicht,
 * Zeilenzahl und Summe — und was mit der Spur geschieht. Derselbe Gedanke wie
 * bei der Handerfassung (R380): Wer hier steht, hat vorher gesehen, was
 * verschwindet.
 */
function Loeschabfrage({
  vorschau,
  laeuft,
  aufLoeschen,
  aufSchliessen,
}: {
  vorschau: Datenimportloeschvorschau
  laeuft: boolean
  aufLoeschen: () => void
  aufSchliessen: () => void
}) {
  const gesperrt = vorschau.hindernis !== null

  return (
    <div className="nachweisschatten" onClick={aufSchliessen}>
      <div className="fenster" role="dialog" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{t('import.loeschen.titel')}</h2>
        </header>

        {gesperrt ? (
          <>
            <p className="probe abweichung">
              {t(vorschau.hindernis!.schluessel as Sprachschluessel, bausteinwerte(vorschau.hindernis!))}
            </p>
            {vorschau.weg && (
              <p className="erlaeuterung">
                {t(vorschau.weg.schluessel as Sprachschluessel, bausteinwerte(vorschau.weg))}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="probe abweichung">
              {t('import.loeschen.was', {
                einheit: gesellschaftsname(vorschau.stand.einheit),
                periode: periodenname(vorschau.stand.periode),
                schicht: bewertungsebenenname(vorschau.stand.ledger),
                zeilen: anzahlwort(vorschau.stand.zeilen),
                summe: betrag(vorschau.stand.summe),
              })}
            </p>

            {/*
              **Was die Zuordnungsebene danach zeigt** (2.3): Sie bleibt liegen
              und teilt weiter auf, was nicht mehr da ist. Der Anwender soll das
              vorher wissen und nicht hinterher suchen.
            */}
            <p className="hinweis">{t('import.loeschen.zuordnung')}</p>

            {/*
              **Der Lauf und die Rohdatei überleben** (R636) — das ist die
              Auflage, unter der hier überhaupt gelöscht werden darf, und sie
              gehört in die Abfrage, nicht in eine Fußnote.
            */}
            <p className="erlaeuterung">{t('import.loeschen.spur')}</p>
          </>
        )}

        <div className="aktionsreihe">
          {!gesperrt && (
            <button type="button" className="gefahr" disabled={laeuft} onClick={aufLoeschen}>
              {t(laeuft ? 'import.loeschen.laeuft' : 'import.loeschen.tun')}
            </button>
          )}
          <button type="button" onClick={aufSchliessen}>
            {t('import.loeschen.abbrechen')}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Die Felder eines Textbausteins als Karte — der Serverbefund in Fachsprache (L6). */
function bausteinwerte(baustein: { werte: { feld: string; wert: string }[] }): Record<string, string> {
  const karte: Record<string, string> = {}
  for (const w of baustein.werte ?? []) {
    karte[w.feld] = w.wert
  }
  return karte
}

/* ======================================================================
   Der neue Datenimport: prüfen, zeigen, übernehmen
   ====================================================================== */

export function Neuerimport({
  vorbelegung,
  aufFertig,
  aufZurueck,
}: {
  vorbelegung: Vorbelegung | null
  /** Gespeichert — mit dem Satz für das Kästchen (R1130) und dem, was die Liste zeigen soll (R1136). */
  aufFertig: (erfolgssatz: string, neu: Neuheit, aufforderungen?: Folgeaufforderung[]) => void
  aufZurueck: () => void
}) {
  const darf = useDarf()
  // Ohne das Recht wird die aufbewahrte Datei nicht gelesen (R1180).
  const aufbewahrungErlaubt = darf('datenimportAusAufbewahrung')
  const [quellen, setQuellen] = useState<Datenquelle[]>([])
  const einheiten = useEinheiten()
  const [quelle, setQuelle] = useState<string>(vorbelegung?.quelle ?? '')
  const [einheit, setEinheit] = useState(vorbelegung?.einheit ?? '')
  /**
   * Worunter dieser Datenimport laufen soll (R374).
   *
   * **Beim Aktualisieren steht der bisherige Name drin** und nicht der
   * Vorschlag: Wer eine Lieferung berichtigt, will sie danach unter demselben
   * Namen wiederfinden. Sonst füllt ihn die geprüfte Vorschau mit ihrem
   * Vorschlag — solange niemand selbst getippt hat.
   */
  //
  // **Und er steht lesbar da** (R948): Ein Name aus der Zeit, als der Vorschlag
  // die Kennung der Gesellschaft trug, zeigt sich hier wie in der Liste — und
  // wird beim Aktualisieren so gespeichert, wie er dasteht.
  const [name, setName] = useState(belegtext(vorbelegung?.name ?? ''))
  /*
    **Getippt ist nur, was auch dasteht.** Im ersten Entwurf stand hier „eine
    Vorbelegung gilt als getippt", und das war einen Fall zu grob: Ein
    Datenimport aus der Zeit vor R374 bringt einen leeren Namen mit, und dann
    bliebe das Feld leer und der Vorschlag käme nie.
  */
  const [nameGetippt, setNameGetippt] = useState((vorbelegung?.name ?? '').trim() !== '')
  const [zeitraum, setZeitraum] = useState(vorbelegung?.periode ?? laufenderMonat())
  /**
   * Die Zielperiode einer Nachbuchung — **gefragt, nicht geerbt** (R613).
   *
   * `null`, solange niemand danach gefragt hat: Die Frage stellt sich erst,
   * wenn spätere Perioden schon geliefert sind (R542). Vorbelegt wird sie dann
   * mit der jüngsten davon.
   */
  const [zielperiode, setZielperiode] = useState<number | null>(null)
  const [vorschaustand, setVorschau] = useState<Importvorschau | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const datei = useRef<HTMLInputElement>(null)
  const [dateiGewaehlt, setDateiGewaehlt] = useState(false)
  /**
   * Wie viele Dateien gewählt sind — **ab zwei läuft der Stapel** (R729).
   *
   * Der Zuschnitt ist die Auswahl selbst und kein Schalter daneben: Wer eine
   * Datei wählt, will einen Monat; wer vierundzwanzig wählt, will keinen
   * Schalter suchen müssen.
   */
  const [anzahlDateien, setAnzahlDateien] = useState(0)
  const [stapelstand, setStapel] = useState<Stapelvorschau | null>(null)
  /**
   * Die geprüfte Mappe eines Reporting Packages (R743).
   *
   * **Die Datei bleibt hier nicht liegen** (R749): Sie ist beim Prüfen
   * archiviert worden, und die Vorschau trägt ihre Nummer. Gebucht wird mit
   * ihr — nicht mit einem zweiten Hochladen.
   */
  const [paketstand, setPaket] = useState<Packagevorschau | null>(null)

  /*
    **Eine gescheiterte Prüfung hält ihren Ausfall fest** (7.2 Abschnitt 39,
    R1210): Bis zum 2026-10-06 setzte der Fehlerzweig Vorschau, Stapel und
    Package auf „nichts". Jetzt gilt der Ausfall, bis eine Prüfung gelingt;
    solange zeigt keine Stelle die Vorschau davor — *Übernehmen* buchte
    sonst die alte.
  */
  const [pruefausfall, setPruefausfall] = useState(false)
  const vorschau = pruefausfall ? null : vorschaustand
  const stapel = pruefausfall ? null : stapelstand
  const paket = pruefausfall ? null : paketstand

  // **Worauf die Lieferung wirkt** (R542) — nur eine Frage, wenn spätere
  // Perioden schon geliefert sind. Die Vorschau sagt, ob das so ist.
  const [wirkung, setWirkung] = useState<'periode' | 'nachbuchung'>('periode')

  // Kein Häkchen „Eintritt in den Konsolidierungskreis“ (R846) und keine
  // Frage der ersten Lieferung (R989) mehr: Der Import kennt keinen Eintritt
  // (5.1 Abschnitt 3.26, R1591), den Zugang bildet die Datenübernahme (R1073).

  useEffect(() => {
    api.datenquellen().then(setQuellen)
  }, [])

  /**
   * Die aufbewahrte Datei, die eingelegt ist (R931) — ihr Name, oder `null`.
   *
   * **Beim Aktualisieren liest die Maske sie sofort:** Die Datei des
   * angemahnten Monats hat sich nicht geändert, nur der Stand davor. Liegt
   * keine oder ist es ein Reporting Package (R933), bleibt die Maske, wie sie
   * war — mit leerem Dateifeld.
   */
  const [aufbewahrt, setAufbewahrt] = useState<string | null>(null)
  /**
   * **Kam die aufbewahrte Datei nicht, steht das am Ort** (7.2 Abschnitt 39,
   * R1210) — mit dem Satz des Dienstes, wo es einen gibt, sonst „Ließ sich
   * nicht laden". Bis zum 2026-10-06 blieb die Maske bei einer Störung stumm.
   * `null`: nichts ausgefallen.
   */
  const [aufbewahrungsausfall, setAufbewahrungsausfall] = useState<string | null>(null)
  const [aufbewahrungsversuch, setAufbewahrungsversuch] = useState(0)

  useEffect(() => {
    if (!vorbelegung?.ausAufbewahrung || !aufbewahrungErlaubt) {
      return
    }

    let gilt = true
    setLaeuft(true)
    setAufbewahrungsausfall(null)
    api
      .datenimportAusAufbewahrung(vorbelegung.ersetzt)
      .then((geprueft) => {
        if (!gilt) {
          return
        }

        setVorschau(geprueft)
        setAufbewahrt(geprueft.dateiname)
      })
      // Das Lesen ist der Inhalt dieser Maske, kein Nachschlagen: Eine
      // Störung meldet das Störungsfenster, ein Befund steht hier (R1003/R1004),
      // und beides mit „Erneut versuchen" (R1210).
      .catch((f) => gilt && setAufbewahrungsausfall(fachlicherSatz(f) ?? ''))
      .finally(() => gilt && setLaeuft(false))
    return () => {
      gilt = false
    }
  }, [vorbelegung, aufbewahrungErlaubt, aufbewahrungsversuch])

  /**
   * Ob die aufbewahrte Datei noch gilt: Niemand hat eine andere gewählt, und
   * Gesellschaft, Periode und Quelle sind die des Datenimports. Wer eine davon
   * ändert, liefert etwas anderes — und dafür braucht es eine Datei.
   */
  const aufbewahrtGilt =
    aufbewahrt !== null &&
    !dateiGewaehlt &&
    vorbelegung !== null &&
    einheit === vorbelegung.einheit &&
    zeitraum === vorbelegung.periode &&
    quelle === (vorbelegung.quelle ?? '')


  const gewaehlt = useMemo(
    () => quellen.find((q) => q.schluessel === quelle) ?? null,
    [quellen, quelle],
  )

  /**
   * Ob diese Quelle ganze Mappen nach der Standardvorlage liest (R743, R745).
   *
   * **Ein Package wird nicht zugeordnet:** Blätter, Spalten und Zellen stehen
   * fest. Deshalb entfallen hier Periode und Name — beide stehen in der Mappe.
   */
  const istPackagequelle = useMemo(
    () => abbildungLesen(gewaehlt?.abbildungJson ?? null).betragsart === 'package',
    [gewaehlt],
  )

  /*
    **Liefert die Datenquelle die Periode, wird sie hier nicht gewählt** (5.1
    Abschnitt 3.42, R937 — Befund vom 2026-10-03). An der Quelle stand „aus
    dem Dateinamen", die Maske zeigte trotzdem ein freies Feld mit dem
    laufenden Monat: Die Juni-Datei der Tochter USA ließ sich in den Oktober
    buchen, und nichts sagte etwas.

    Gelesen wird im Rechenkern: aus dem Dateinamen, sobald die Datei
    gewählt ist; aus einer festen Zelle beim Prüfen.
  */
  const periodenquelle = useMemo(() => {
    const a = abbildungLesen(gewaehlt?.abbildungJson ?? null)
    return { herkunft: a.periodenherkunft, zelle: a.periodenzelle }
  }, [gewaehlt])
  /** Der Name der einen gewählten Datei — `null` ohne Datei und im Stapel. */
  const [dateiname, setDateiname] = useState<string | null>(null)
  const [gelesen, setGelesen] = useState<Quellperiode | null>(null)
  const liefertPeriode = periodenquelle.herkunft !== '' && anzahlDateien <= 1 && !istPackagequelle

  useEffect(() => {
    setGelesen(null)
    if (periodenquelle.herkunft !== 'dateiname' || !gewaehlt || dateiname === null) {
      return
    }

    let gilt = true
    // Ein Nachschlagen im Hintergrund (R1007) — die Prüfung liest die Periode ohnehin.
    imHintergrund(() => api.periodeDerLieferung(gewaehlt.schluessel, dateiname, vorbelegung?.periode ?? null))
      .then((q) => {
        if (!gilt) {
          return
        }

        setGelesen(q)
        // Nur eine Periode, die gilt, wird die des Laufs (R938, R939).
        if (q.periode !== null && q.befund === null) {
          setZeitraum(q.periode)
        }
      })
      .catch(() => undefined)
    return () => {
      gilt = false
    }
  }, [periodenquelle.herkunft, gewaehlt, dateiname, vorbelegung])

  /** Was an der Stelle des Periodenfeldes steht, wenn die Quelle die Periode liefert (R937). */
  const periodenangabe = (() => {
    // Beim Aktualisieren aus der aufbewahrten Datei steht die Periode fest.
    if (vorbelegung && !dateiGewaehlt) {
      return periodenname(zeitraum)
    }

    if (periodenquelle.herkunft === 'dateiname') {
      return gelesen?.periode != null && gelesen.befund === null
        ? t('import.periode.gelesen.dateiname', { periode: periodenname(gelesen.periode) })
        : t('import.periode.kommt.dateiname')
    }

    return vorschau !== null && vorschau.gelesen > 0
      ? t('import.periode.gelesen.zelle', { periode: periodenname(vorschau.periode), zelle: periodenquelle.zelle })
      : t('import.periode.kommt.zelle', { zelle: periodenquelle.zelle })
  })()

  /**
   * Ob die Periode feststeht — gewählt, aus dem Dateinamen gelesen oder
   * beim Aktualisieren mitgebracht. Steht sie in einer Zelle, sagt es erst
   * die Prüfung, und die bringt den Namen ohnehin mit.
   */
  const periodeSteht =
    !liefertPeriode ||
    (vorbelegung !== null && !dateiGewaehlt) ||
    (periodenquelle.herkunft === 'dateiname' && gelesen?.periode != null && gelesen.befund === null)

  /*
    **Der Name steht da, sobald Datenquelle, Gesellschaft und Periode
    feststehen** (5.1 Abschnitt 3.46, R988) — nicht erst nach der Prüfung.
    Der Befund vom 2026-10-03: Das Feld stand leer, obwohl alles, woraus der
    Name besteht, schon gewählt war. Gebildet wird er im Rechenkern wie nach
    der Prüfung (R374); wer selbst getippt hat, behält seinen Namen (R948).

    **Eine Periode, die erst aus der Datei kommt, steht noch nicht fest:**
    Bis die Datei gewählt ist, gälte sonst der laufende Monat, und der Name
    nennte einen Monat, den niemand gewählt hat.
  */
  useEffect(() => {
    if (nameGetippt || !quelle || !einheit || !zeitraum || !periodeSteht) {
      return
    }

    let gilt = true
    // Ein Vorschlag im Hintergrund (R1007): Fehlt er, tippt der Anwender den Namen.
    imHintergrund(() => api.namensvorschlag(einheit, zeitraum, wirkung))
      .then((v) => gilt && setName(belegtext(v.name)))
      .catch(() => undefined)

    return () => {
      gilt = false
    }
  }, [quelle, einheit, zeitraum, wirkung, nameGetippt, periodeSteht])

  /*
    **Beide Auswahllisten suchen** (R583, 7.2 Abschnitt 10). Die Gesellschaft
    ist die gemeldete Stelle: Bei 200 Gesellschaften ist eine Klappliste kein
    Griff mehr, sondern eine Suche mit den Augen. Die Quelle steht daneben und
    wächst mit — jede liefernde Vorsystemschnittstelle ist eine Zeile.
  */
  const quellenwahl = useMemo(
    () =>
      quellen
        .filter((q) => q.istAktiv)
        .map((q) => ({
          wert: q.schluessel,
          text: `${mitCode(q.code, beschriftung(q.namen), ' — ', q.schluessel)} (${artname(q.art)})`,
          suchtext: q.schluessel,
        })),
    [quellen],
  )

  const einheitenwahl = useMemo(() => einheiten.map(ausStammdatum), [einheiten])

  // **Die Quelle belegt die Gesellschaft nicht vor** (R521): Sie ist eine
  // Angabe des Laufs — eine Quelle gilt für jede Gesellschaft, die so liefert.
  // **Die Bewertungsebene dagegen sagt die Quelle** (R537): als fester Wert
  // oder aus einer Spalte der Datei. Hier gibt es dafür nichts zu wählen.

  // Ohne Datei ist nichts zu prüfen — der Knopf sagt es, statt ins Leere zu
  // klicken und den Anwender raten zu lassen, was fehlt.
  //
  // **Und ohne gültige Periode auch nicht** (R938, R939): Nennt der Dateiname
  // keine oder eine andere als die des Datenimports, steht der Grund darunter.
  const bereit =
    gewaehlt !== null &&
    gewaehlt.istAusfuehrbar &&
    einheit !== '' &&
    (dateiGewaehlt || aufbewahrtGilt) &&
    !(liefertPeriode && gelesen?.befund)

  /*
    **Der Prüfknopf steht nur da, wo sein Weg erlaubt ist** (R1180) — derselbe
    Knopf liest je nach Quelle und Auswahl ein Package, einen Stapel, die
    aufbewahrte Datei oder eine einzelne Datei.
  */
  const pruefenErlaubt = istPackagequelle
    ? darf('packagevorschau')
    : anzahlDateien > 1
      ? darf('stapelvorschau')
      : !dateiGewaehlt && aufbewahrtGilt
        ? aufbewahrungErlaubt
        : darf('importvorschau')

  /**
   * Der Stapel: alle gewählten Dateien lesen, ohne etwas zu buchen (R729).
   *
   * **Die Periode wird hier nicht mitgeschickt.** Sie steht in jeder Datei —
   * im Namen oder in einer Zelle, je nachdem, was an der Quelle gebunden ist
   * (R728). Sagt die Quelle nichts dazu, kommt der Stapel mit einem Hindernis
   * zurück und nicht mit vierundzwanzig Lieferungen desselben Monats.
   */
  async function stapelPruefen(gewaehlte: File[]) {
    if (!gewaehlt) {
      return
    }

    setLaeuft(true)
    setMeldung(null)
    setVorschau(null)

    const felder = new FormData()
    for (const d of gewaehlte) {
      felder.append('dateien', d)
    }
    felder.append('quelle', gewaehlt.schluessel)
    felder.append('einheit', einheit)

    try {
      setStapel(await api.stapelvorschau(felder))
      setPruefausfall(false)
    } catch (f) {
      // Nur der fachliche Befund steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004). Ausgefallen, nicht
      // leer (R1210) — siehe `pruefausfall`.
      setMeldung(fachlicherSatz(f))
      setPruefausfall(true)
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Ein Reporting Package: eine Mappe, viele Blätter, eine Klammer (R743).
   *
   * **Die Periode wird hier nicht mitgeschickt.** Sie steht im Deckblatt der
   * Mappe, und die Gesellschaft daneben wird mit ihm verglichen: Wer das
   * Package der Tochter auf die Holding lädt, erfährt es vor dem Buchen.
   */
  async function packagePruefen(gewaehlteDatei: File) {
    if (!gewaehlt) {
      return
    }

    setLaeuft(true)
    setMeldung(null)
    setVorschau(null)
    setStapel(null)

    const felder = new FormData()
    felder.append('datei', gewaehlteDatei)
    felder.append('quelle', gewaehlt.schluessel)
    felder.append('einheit', einheit)

    try {
      setPaket(await api.packagevorschau(felder))
      setPruefausfall(false)
    } catch (f) {
      setMeldung(fachlicherSatz(f))
      setPruefausfall(true)
    } finally {
      setLaeuft(false)
    }
  }

  async function pruefen(
    gewuenschteWirkung: 'periode' | 'nachbuchung' = wirkung,
    gewuenschteZielperiode: number | null = zielperiode,
  ) {
    const gewaehlteDateien = [...(datei.current?.files ?? [])]
    /*
      **Die Quelle sagt, was für eine Lieferung das ist** (R745). Ein
      Reporting Package ist eine ganze Mappe nach fester Vorlage; ein
      Häkchen daneben fragte nach etwas, das an der Quelle schon steht.
    */
    if (istPackagequelle && gewaehlteDateien[0]) {
      await packagePruefen(gewaehlteDateien[0])
      return
    }

    // **Ab zwei Dateien ist es ein Stapel** (R729) — derselbe Knopf, ein
    // anderer Weg: erst alle lesen, zuordnen, dann aufsteigend buchen.
    if (gewaehlteDateien.length > 1) {
      await stapelPruefen(gewaehlteDateien)
      return
    }

    /*
      **Ohne gewählte Datei wird die aufbewahrte neu gelesen** (R931) — nach
      dem Zuordnen eines Kontos zum Beispiel, oder wenn die Wirkung wechselt.
      Die Angaben des Anwenders reisen mit; die Datei bleibt, wo sie ist.
    */
    if (gewaehlteDateien.length === 0 && aufbewahrtGilt && vorbelegung) {
      setLaeuft(true)
      setMeldung(null)
      try {
        const geprueft = await api.datenimportAusAufbewahrung(vorbelegung.ersetzt, {
          wirkung: gewuenschteWirkung,
          berichtsperiode: gewuenschteWirkung === 'nachbuchung' ? gewuenschteZielperiode : null,
        })
        setVorschau((bisher) => mitBisherigenAntworten(geprueft, bisher))
        setPruefausfall(false)
      } catch (f) {
        setMeldung(fachlicherSatz(f))
        setPruefausfall(true)
      } finally {
        setLaeuft(false)
      }
      return
    }

    const gewaehlteDatei = gewaehlteDateien[0]
    if (!gewaehlteDatei || !gewaehlt) {
      return
    }

    setStapel(null)
    setPaket(null)
    setLaeuft(true)
    setMeldung(null)


    const felder = new FormData()
    felder.append('datei', gewaehlteDatei)
    felder.append('quelle', gewaehlt.schluessel)
    felder.append('einheit', einheit)
    felder.append('periode', String(zeitraum))
    // Beim Aktualisieren steht die Periode fest — der Rechenkern hält die der
    // Datei dagegen (R939).
    if (vorbelegung) {
      felder.append('aktualisiert', String(vorbelegung.periode))
    }
    felder.append('wirkung', gewuenschteWirkung)
    /*
      **Die Zielperiode einer Nachbuchung wird gefragt** (R613): Sie geht nur
      mit, wenn jemand sie gewählt hat. Ohne Angabe nimmt der Rechenkern die
      jüngste betroffene Folgeperiode (R542) — dieselbe, mit der das Feld
      vorbelegt wird.
    */
    if (gewuenschteWirkung === 'nachbuchung' && gewuenschteZielperiode !== null) {
      felder.append('berichtsperiode', String(gewuenschteZielperiode))
    }

    try {
      const geprueft = await api.importvorschau(felder)

      // **Die Periode aus der Zelle kennt erst die Prüfung** (R937) — sie ist
      // ab jetzt die des Laufs, wie die aus dem Dateinamen.
      if (liefertPeriode && geprueft.gelesen > 0) {
        setZeitraum(geprueft.periode)
      }

      // **Eine Antwort überlebt das erneute Lesen** (R873): Wer annimmt und dann
      // im Zuordnungsfenster ein Konto nachträgt, soll nicht zweimal antworten.
      setVorschau((bisher) => mitBisherigenAntworten(geprueft, bisher))
      setPruefausfall(false)

      /*
        **Der Vorschlag kommt vom Rechenkern und nicht von hier** (R374): Er
        bildet ihn aus Gesellschaft, Periode und Wirkung, und derselbe Satz
        steht am Beleg, wenn niemand tippt — der nächtliche Lauf zum Beispiel.
        Eine zweite Formulierung an dieser Stelle liefe irgendwann auseinander.

        Wer selbst getippt hat, behält seinen Namen, auch wenn er die Periode
        noch einmal ändert und neu prüft.
      */
      if (!nameGetippt) {
        // Gespeichert wird, was im Feld steht (R948) — also steht der
        // Vorschlag so da, wie die Liste ihn zeigen wird: mit Monat und Jahr.
        setName(belegtext(geprueft.namensvorschlag))
      }
    } catch (f) {
      setMeldung(fachlicherSatz(f))
      setPruefausfall(true)
    } finally {
      setLaeuft(false)
    }
  }

  async function uebernehmen() {
    if (!vorschau) {
      return
    }

    setLaeuft(true)
    try {
      const kopf = await api.importUebernehmen(
        vorschau,
        gewaehlt?.schluessel ?? null,
        null,
        vorbelegung?.ersetzt ?? null,
        name.trim(),
      )
      /*
        **Das Kästchen sagt, dass es gelang, und sonst nichts** (7.2 Abschnitt
        34, R1131): Bis zum 2026-10-06 stand hier „28 Zeilen übernommen —
        Tochter USA, September 2026." — *„Ob 28 oder 30 oder 40 Zeilen, die
        Aussage ist ohne Mehrwert für den Anwender."*

        **Die Aufforderung bleibt** (R879, R1132): Dass die Folgeperiode nicht
        neu abgeleitet wurde, ist eine Handlungsanweisung. Sie steht im
        Kästchen, das dann stehen bleibt, mit einem Knopf zu den Datenimporten
        dieser Periode. Die Antworten auf Vorschläge stehen weiter am Lauf.
      */
      const folgesatz = (kopf.uebernahmesaetze ?? []).find((s) => s.includes('(R641)'))
      aufFertig(
        t('erfolg.datenimport.gespeichert'),
        { laeufe: [kopf.id] },
        kopf.folgeperiodeOffen && folgesatz
          ? [{ satz: belegtext(folgesatz), einheit: kopf.einheit, periode: kopf.folgeperiodeOffen }]
          : [],
      )
    } catch (f) {
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <section>
      <div className="feldzeile">
        <h1>{vorbelegung ? t('import.stand.aktualisieren') : t('import.neu')}</h1>
        <button type="button" onClick={aufZurueck}>
          {t('import.stand.zurueck')}
        </button>
      </div>

      {/*
        **Der Satz sagt, was diese Übernahme anfassen wird** (R639, R145).
        Beide Fälle stehen da, weil beide eine Neuigkeit sind: Bis zum
        2026-09-20 ersetzte jede Übernahme alles in ihrer Klammer, und es gab
        gar keinen Weg, etwas hinzuzufügen.
      */}
      <p className="erlaeuterung">
        {vorbelegung
          ? t('import.ersetzt.hinweis', {
              // Der gespeicherte Belegname, mit Namen gelesen (R838, Entscheidung 2)
              // und mit Monat und Jahr (R839, Entscheidung 3).
              bezeichnung: belegtext(vorbelegung.name),
              zeilen: anzahlwort(vorbelegung.zeilen),
              periode: periodenname(vorbelegung.periode),
            })
          : t('import.neu.hinweis')}
      </p>

      {/*
        **Die Angaben des Laufs stehen in einer Zeile** (R608): Vier Felder
        untereinander brauchten 257 Pixel und nutzten ein Viertel der Breite.
        Hier stehen sie nebeneinander — und der Knopf, der sie benutzt, steht
        dabei und nicht auf einer eigenen Zeile darunter.
      */}
      <div className="feldzeile">
        <label>
          <span className="name">{t('import.schritt.quelle')}</span>
          <Auswahlfeld
            wert={quelle}
            eintraege={quellenwahl}
            aufWahl={setQuelle}
            leer={t('import.quelle.waehlen')}
            beschriftung={t('import.schritt.quelle')}
          />
        </label>

        <label>
          <span className="name">{t('import.spalte.einheit')}</span>
          <Auswahlfeld
            wert={einheit}
            eintraege={einheitenwahl}
            aufWahl={setEinheit}
            leer="—"
            beschriftung={t('import.spalte.einheit')}
          />
        </label>

        {/*
          MM.JJJJ wie jede Periodeneingabe in der Anwendung (R502) — **aber
          nicht im Stapel** (R728): Dort bringt jede Datei ihre Periode mit,
          und ein Feld, das dann nichts entscheidet, stünde nur da.
        */}
        {anzahlDateien <= 1 && !liefertPeriode && (
          <Periodenfeld
            wert={zeitraum}
            aufWert={(p) => p !== null && setZeitraum(p)}
            beschriftung={t('import.schritt.periode')}
          />
        )}

        {/*
          **Sichtbar, nicht änderbar** (R937): Man sieht vor dem Buchen, wohin
          gebucht wird — und woher die Angabe kommt.
        */}
        {liefertPeriode && (
          <div className="festangabe">
            <span className="name">{t('import.schritt.periode')}</span>
            <span className="wert">{periodenangabe}</span>
          </div>
        )}


        {/*
          **Mehrere Dateien sind erlaubt, und das ist der ganze Schalter**
          (R729): Ab zwei läuft der Stapel. Ein Häkchen „Stapelimport" daneben
          fragte nach etwas, das die Auswahl schon beantwortet hat.
        */}
        {/*
          Ein `div` und kein `label`: Die Dateiwahl bringt ihr eigenes mit
          (R949), und ein `label` im `label` gibt es nicht.
        */}
        {/* Mehrere Dateien nur, wer einen Stapel lesen darf; gar keine, wer nichts lesen darf (R1180). */}
        {(darf('importvorschau') || darf('stapelvorschau') || darf('packagevorschau')) && (
          <div className="feld">
            <span className="name">{t('import.schritt.datei')}</span>
            <Dateiwahl
              beschriftung={t('dateiwahl.waehlen.mehrere')}
              eingabe={datei}
              mehrere={darf('stapelvorschau')}
              accept=".xlsx,.csv,.txt"
              aufWahl={(dateien) => {
                const anzahl = dateien.length
                setDateiGewaehlt(anzahl > 0)
                setAnzahlDateien(anzahl)
                setDateiname(anzahl === 1 ? (dateien[0]?.name ?? null) : null)
                // **Eine gewählte Datei löst die aufbewahrte ab** (R931): Ihre
                // Vorschau bliebe sonst stehen, und *Übernehmen* buchte die alte.
                if (anzahl > 0 && aufbewahrt !== null) {
                  setVorschau(null)
                }
                if (anzahl <= 1) {
                  setStapel(null)
                }
              }}
            />
          </div>
        )}


        {/*
          **Jeder Datenimport trägt einen Namen** (R374, seit dem 2026-09-20).
          Er steht bei den Angaben der Lieferung und nicht unten am
          Übernehmen-Knopf: Er beschreibt sie, wie Gesellschaft und Periode es
          tun. Leer bleibt er nur, bis die Prüfung den Vorschlag mitbringt —
          wer vorher selbst tippt, behält seinen (R145).
        */}
        {/*
          **Im Stapel steht hier nichts** (R374, R732): Jeder Monat wird ein
          eigener Datenimport und trägt den Vorschlag des Rechenkerns —
          vierundzwanzig Zeilen unter demselben getippten Namen wären in der
          Liste nicht auseinanderzuhalten.
        */}
        {anzahlDateien <= 1 && (
          <label>
            <span className="name">{t('import.spalte.belegname')}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setNameGetippt(true)
                setName(e.target.value)
              }}
            />
          </label>
        )}


        {pruefenErlaubt && (
          <button
            type="button"
            disabled={!bereit || laeuft}
            onClick={() => void pruefen(wirkung, zielperiode)}
          >
            {t(laeuft ? 'import.pruefterade' : 'import.pruefen')}
          </button>
        )}
      </div>

      {/*
        **Der Befund bleibt, die Erklärung geht** (R608, R145): Dass aus dieser
        Quelle heute nichts zu lesen ist, muss dastehen — der Absatz darunter
        erklärte, was eine Datenbankquelle ist, und das beantwortet keine Frage,
        die hier aufkommt.
      */}
      {gewaehlt && !gewaehlt.istAusfuehrbar && (
        <p className="hinweis">
          {t('import.nichtausfuehrbar.titel', { art: artname(gewaehlt.art) })}
        </p>
      )}

      {meldung && <p className="hinweis">{meldung}</p>}

      {/*
        **Eine gescheiterte Prüfung ist keine fehlende Vorschau** (7.2
        Abschnitt 39, R1210): Ohne fachlichen Satz steht hier, dass sie nicht
        kam — mit dem Knopf, der dieselbe Prüfung noch einmal anstößt.
      */}
      {pruefausfall && !laeuft && !meldung && <Abrufausfall abruf={{ erneut: () => void pruefen() }} />}

      {/*
        **Der gesperrte Prüfknopf sagt warum** (R938, R939, R144): keine
        Periode im Dateinamen, oder eine andere als die des Datenimports.
      */}
      {liefertPeriode && gelesen?.befund && <p className="hinweis">{belegtext(gelesen.befund)}</p>}

      {/* Woher die Vorschau kommt, wenn niemand eine Datei gewählt hat (R931, R145). */}
      {aufbewahrungsausfall !== null && !laeuft && (
        <Abrufausfall
          abruf={{ erneut: () => setAufbewahrungsversuch((n) => n + 1) }}
          satz={aufbewahrungsausfall || undefined}
        />
      )}
      {aufbewahrtGilt && (
        <p className="erlaeuterung">{t('import.aktualisierung.eingelegt', { datei: aufbewahrt })}</p>
      )}

      {vorschau && vorschau.folgeperioden && vorschau.folgeperioden.length > 0 && (
        <fieldset className="wirkung">
          <legend>{t('import.wirkung.frage', { perioden: vorschau.folgeperioden.map(periodenname).join(', ') })}</legend>
          <label>
            <input
              type="radio"
              name="wirkung"
              checked={wirkung === 'periode'}
              onChange={() => {
                setWirkung('periode')
                void pruefen('periode')
              }}
            />
            {t('import.wirkung.periode', { periode: periodenname(zeitraum), folge: periodenname(vorschau.folgeperioden[0]!) })}
          </label>
          <label>
            <input
              type="radio"
              name="wirkung"
              checked={wirkung === 'nachbuchung'}
              onChange={() => {
                setWirkung('nachbuchung')
                // Vorbelegt mit der jüngsten betroffenen Periode — das ist,
                // was der Rechenkern ohne Angabe ohnehin nähme (R542).
                const vorbelegt = Math.max(...vorschau.folgeperioden!)
                setZielperiode(vorbelegt)
                void pruefen('nachbuchung', vorbelegt)
              }}
            />
            {t('import.wirkung.nachbuchung', {
              periode: periodenname(zeitraum),
              ziel: periodenname(zielperiode ?? vorschau.zielperiode ?? Math.max(...vorschau.folgeperioden)),
            })}
          </label>

          {/*
            **Die Zielperiode wird gefragt, nicht geerbt** (R613, seit dem
            2026-09-18 nachmittags). Bis dahin war sie die Berichtsperiode der
            Kontextzeile — ein Feld am Kopf des Bildschirms, das niemand mit
            dieser Frage in Verbindung brachte, und das es hier nicht mehr gibt.
          */}
          {wirkung === 'nachbuchung' && (
            <Periodenfeld
              wert={zielperiode ?? Math.max(...vorschau.folgeperioden)}
              aufWert={(p) => {
                if (p !== null) {
                  setZielperiode(p)
                  void pruefen('nachbuchung', p)
                }
              }}
              beschriftung={t('import.wirkung.zielperiode')}
            />
          )}
        </fieldset>
      )}

      {/*
        **Der Stapel tritt an die Stelle der Vorschau** (R729): Es ist
        derselbe Schritt — lesen, zeigen, zuordnen, dann buchen —, nur für
        viele Dateien und mit einer Zeile je Monat statt einer Zeile je Konto.
      */}
      {paket && (
        <Packageliste
          vorschau={paket}
          einheit={einheit}
          laeuft={laeuft}
          aufNeuPruefen={() => void pruefen()}
          aufFertig={(laeufe) => aufFertig(t('erfolg.package.gespeichert'), { laeufe })}
        />
      )}

      {stapel && (
        <Stapelliste
          vorschau={stapel}
          einheit={einheit}
          quelle={gewaehlt?.schluessel ?? null}
          vorschlaege={abbildungLesen(gewaehlt?.abbildungJson ?? null).vorschlaege}
          laeuft={laeuft}
          aufNeuPruefen={() => void pruefen()}
          aufFertig={(laeufe) => aufFertig(t('erfolg.datenimporte.gespeichert'), { laeufe })}
        />
      )}

      {vorschau && (
        <Vorschau

          vorschau={vorschau}
          laeuft={laeuft}
          einheit={einheit}
          periode={zeitraum}
          name={name}
          aufUebernehmen={uebernehmen}
          aufNeuPruefen={() => void pruefen()}
          aufAntwort={(index, angenommen) =>
            setVorschau((v) =>
              v && {
                ...v,
                vorschlaege: (v.vorschlaege ?? []).map((x, i) => (i === index ? { ...x, angenommen } : x)),
              },
            )
          }
        />
      )}

    </section>
  )
}

/**
 * Was die Prüfung ergeben hat — vor der Übernahme und nicht danach.
 *
 * **Die Zahlen stehen über der Tabelle**, weil die erste Frage nicht „was
 * steht in Zeile 14" lautet, sondern „geht das auf und wie viel kommt an".
 */
export function Vorschau({
  vorschau,
  laeuft,
  einheit,
  periode,
  name,
  aufUebernehmen,
  aufNeuPruefen,
  aufAntwort,
}: {
  vorschau: Importvorschau
  laeuft: boolean
  einheit: string
  periode: number
  /** Nur zum Sperren und Begründen — getippt wird er in der Feldzeile oben (R374). */
  name: string
  aufUebernehmen: () => void
  aufNeuPruefen: () => void
  /** Die Antwort auf einen Vorschlag der Datenquelle (R873). */
  aufAntwort: (index: number, angenommen: boolean) => void
}) {
  const beantwortet = alleBeantwortet(vorschau.vorschlaege)
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('datenimport:vorschau')

  const darf = useDarf()
  const [ordnetZu, setOrdnetZu] = useState<string | null>(null)
  /** Die Rückfrage nach dem Monat, wo der Server ihn verlangt (5.1 Abschnitt 3.51, R1173). */
  const monatsfrage = useMonatsfrage()
  /** Das Fenster, in dem zugeordnet wird — offen oder zu (R589). */
  const [fensterOffen, setFensterOffen] = useState(false)

  /*
    **Der Knopf am Befund zeigt nur die Zeilen, die ihn tragen** (5.1
    Abschnitt 3.41, R935). Gibt es nach einer neuen Prüfung keine mehr, stehen
    wieder alle da — eine leere Tabelle wäre eine Sackgasse.
  */
  const [nurBefunde, setNurBefunde] = useState(false)
  const mitBefund = vorschau.zeilen.filter((z) => sperrart(z) === 'befund')
  // Was die Lieferung nicht nennt, steht null und wird kommentarlos so
  // gebucht (5.1 Abschnitt 3.54, R1194) — die Tabelle zeigt die Datei.
  const sichtbar = nurBefunde && mitBefund.length > 0 ? mitBefund : vorschau.zeilen.filter((z) => !z.weggefallen)

  /*
    **Wie viele Konten offen sind, sagt die Vorschau** — je lokaler Nummer
    einmal und nicht je Zeile: Eine SuSa führt dasselbe Konto mehrfach, wenn
    sie nach Bewegungsart aufteilt (R590).
  */
  const offen = useMemo(() => offeneKonten(vorschau), [vorschau])

  /*
    **Gezählt wird alles Offene, nicht nur die Konten** (R595, seit dem
    2026-09-18). Ein Knopf, der zwei offene Konten nennt und die drei offenen
    Bewegungsartcodes verschweigt, schickt den Anwender in ein Fenster, das
    mehr enthält, als er erwartet hat — und die Frage „bin ich fertig?" hätte
    zwei Antworten.
  */
  const offeneArten = useMemo(() => offeneBewegungsarten(vorschau), [vorschau])
  // Und die Werte, die ihren Vorrat nicht gefunden haben (R597) — je Feld und
  // Code einer, über alle Felder gezählt.
  const offeneWertzahl = useMemo(
    () => [...offeneWerte(vorschau).values()].reduce((summe, werte) => summe + werte.length, 0),
    [vorschau],
  )
  const offenGesamt = offen.length + offeneArten.length + offeneWertzahl

  /**
   * Einen Vorschlag annehmen — und damit ein Stammdatum schreiben.
   *
   * **Der Vorschlag wird nie von selbst wirksam** (R5, R138), auch nicht bei
   * hundertprozentiger Übereinstimmung: Ein falsch zugeordnetes Konto läuft in
   * jeder Folgeperiode weiter. Angenommen wird er hier, wo er auffällt — und
   * die Zuordnung gilt ab dieser Periode auch für jede künftige Lieferung.
   */
  async function zuordnen(zeile: Importvorschau['zeilen'][number], ab?: number) {
    if (!zeile.vorschlag) {
      return
    }

    setOrdnetZu(zeile.lokalnummer)
    try {
      await api.zuordnen(einheit, zeile.lokalnummer, zeile.bezeichnung, zeile.vorschlag, periode, { wirksamAb: ab })
      aufNeuPruefen()
    } catch (f) {
      // War das Konto schon einmal zugeordnet, fragt das Fenster nach dem Monat (R1173).
      const absage = monatFehlt(f)
      if (!absage) {
        throw f
      }
      monatsfrage.frage(absage, (monat) => void zuordnen(zeile, monat))
    } finally {
      setOrdnetZu(null)
    }
  }

  return (
    <div className="abschnitt">
      <h2>{t('import.vorschau.titel', { datei: vorschau.dateiname })}</h2>

      {monatsfrage.fenster}

      {/*
        **Solange das Fenster offen ist, sind diese Zahlen alt** (R591) — und
        sie sagen das. Neu gerechnet wird einmal, beim Schließen; eine Zahl,
        die still veraltet, ist schlimmer als keine (R145).
      */}
      <div className={fensterOffen ? 'kennzahlen veraltet' : 'kennzahlen'}>
        <Zahlkachel name={t('import.spalte.gelesen')} wert={vorschau.gelesen} />
        <Zahlkachel name={t('import.vorschau.gueltig')} wert={vorschau.gueltig} />
        <Zahlkachel name={t('import.vorschau.fehlerhaft')} wert={vorschau.fehlerhaft} />
        <div className="kennzahl">
          <span className="name">{t('import.vorschau.probe')}</span>
          <span className="wert">
            <Zustand
              art={vorschau.gehtAuf ? 'fertig' : vorschau.fehlerhaft > 0 ? 'offen' : 'blockiert'}
              text={
                vorschau.gehtAuf
                  ? t('import.vorschau.gehtauf')
                  : vorschau.fehlerhaft > 0
                    ? t('import.vorschau.stehtaus')
                    : t('import.vorschau.differenz', { betrag: betrag(vorschau.summe) })
              }
            />
          </span>
        </div>
      </div>

      {fensterOffen && <p className="hinweis">{t('import.zuordnung.veraltet')}</p>}

      {/*
        **Die Vorschau zeigt Zahlen und Zuordnung, keine Sätze** (5.1
        Abschnitt 3.48, R992 — Befund vom 2026-10-03: *„Sie haben hier keinen
        Mehrwert."*). Die Sätze des Rechenkerns stehen nur noch, wenn aus der
        Datei nichts zu lesen war — dann stünde sonst eine leere Seite ohne
        Grund da (R145). Und sie lesen sich mit Monat und Jahr (R839).
      */}
      {vorschau.gelesen === 0 &&
        vorschau.meldungen.map((m, i) => (
          <p key={i} className="erlaeuterung">
            {belegtext(m)}
          </p>
        ))}

      {/*
        **Der Weg steht hier und führt nicht in einen anderen Bereich** (R589).
        Bis zum 2026-09-18 gab es ihn nur als Knopf an der Zeile, und nur dann,
        wenn ein Vorschlag da war — keinen Vorschlag hat aber gerade das neue
        Konto, für das keine der drei Quellen (R138) etwas sagen kann.
      */}
      {/*
        **Die beiden Handgriffe der Lieferung stehen beieinander** (R609, seit
        dem 2026-09-18): *Zuordnen* und *Übernehmen* gehören derselben Datei,
        und bis dahin trennte sie die Tabelle — bei dreißig Zeilen lag der
        zweite Knopf 672 Pixel unter dem ersten. Sie stehen bei den Zahlen,
        weil die erste Frage „geht das auf und wie viel kommt an" lautet und
        nicht „was steht in Zeile 14".
      */}
      {/*
        **Die Vorschläge der Datenquelle stehen über dem Knopf, der auf sie
        wartet** (5.1 Abschnitt 3.35, R873): je Vorschlag einmal, nicht je Zeile.
      */}
      <Vorschlagsfrage
        vorschlaege={vorschau.vorschlaege ?? []}
        mitUmfang
        laeuft={laeuft}
        aufAntwort={aufAntwort}
      />

      <p className="handreichung">
        {/* Im Fenster wird zugeordnet; wer das nicht darf, bekommt den Weg nicht (R1180). */}
        {darf('zuordnen') && offenGesamt > 0 && (
          <button
            type="button"
            disabled={laeuft}
            onClick={() => setFensterOffen(true)}
          >
            {/* Die Aufrufstelle wählt die Zahlform — ein Paar je Zähler, wie am Stammdatenblatt. */}
            {t(
              offenGesamt === 1
                ? 'import.zuordnung.oeffnen.eins'
                : 'import.zuordnung.oeffnen.mehrere',
              { anzahl: anzahlwort(offenGesamt) },
            )}
          </button>
        )}{' '}
        {darf('importUebernehmen') && (
          <button
            type="button"
            className="haupt"
            disabled={laeuft || !vorschau.gehtAuf || name.trim() === '' || !beantwortet}
            onClick={aufUebernehmen}
          >
            {/* Ein Paar je Zähler — „1 Zeilen übernehmen" stand hier bis zum 2026-09-18. */}
            {t(vorschau.gueltig === 1 ? 'import.uebernehmen.eins' : 'import.uebernehmen', {
              zeilen: anzahlwort(vorschau.gueltig),
            })}
          </button>
        )}

        {/*
          **Nur der gesperrte Knopf sagt warum** (R144, R608): Steht er offen,
          war der Satz daneben eine Beschreibung dessen, was der Knopf ohnehin
          tut — und die beantwortet keine Frage, die im Lauf aufkommt.
        */}
        {/*
          **Und er nennt den wahren Grund** (R935): Bis zum 2026-10-03 stand
          hier für jede gesperrte Zeile „keinem Konzernkonto zugeordnet" —
          auch wenn das Konto dastand und etwas anderes fehlte.
        */}
        {!vorschau.gehtAuf && vorschau.fehlerhaft > 0 && (
          <Sperrgruende
            vorschau={vorschau}
            nurBefunde={nurBefunde && mitBefund.length > 0}
            aufNurBefunde={setNurBefunde}
          />
        )}
        {!vorschau.gehtAuf && vorschau.fehlerhaft === 0 && (
          <span className="erlaeuterung">
            {' '}
            {t('import.uebernehmen.differenz', { betrag: betrag(vorschau.summe) })}
          </span>
        )}

        {/*
          **Und wenn nur der Name fehlt, steht das auch da** (R144): Ein
          gesperrter Knopf ohne Grund schickt den Anwender auf die Suche.
        */}
        {darf('importUebernehmen') && vorschau.gehtAuf && name.trim() === '' && (
          <span className="erlaeuterung"> {t('import.name.fehlt')}</span>
        )}
        {darf('importUebernehmen') && vorschau.gehtAuf && name.trim() !== '' && !beantwortet && (
          <span className="erlaeuterung"> {t('import.uebernehmen.vorschlaege')}</span>
        )}
      </p>

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            {/*
              **Vier Spalten: Lokales Konto, Bezeichnung, Konzernkonto, Betrag**
              (R992 — Entscheidung des Auftraggebers vom 2026-10-03). Zeile,
              letzter Stand, Verkehrszahl, Bewegungsart und Befund sind fort; die
              Zuordnung, die im Befund stand, steht unter dem Konzernkonto.
            */}
            <tr>
              <th>{t('import.spalte.lokalkonto')}</th>
              <th>{t('import.spalte.bezeichnung')}</th>
              <th>{t('import.spalte.konzernkonto')}</th>
              <th className="zahl">{t('import.spalte.betrag')}</th>
            </tr>
          </thead>
          <tbody>
            {sichtbar.map((z, i) => (
              <tr key={`${z.nummer}-${i}`} className={z.status === 'gueltig' ? undefined : 'geaendert'}>
                <td className="code">{z.lokalnummer}</td>
                <td>{z.bezeichnung ?? '—'}</td>
                <td>
                  {z.konto ? kontobezeichnung(z.konto) : '—'}
                  {/* Warum diese Zeile nicht übernommen werden kann (R935). */}
                  {/* Ein Satz des Rechenkerns, ohne Regelnummer gelesen (7.2 Abschnitt 34, R1133). */}
                  {z.status !== 'gueltig' && z.meldung && <span className="erlaeuterung"> {belegtext(z.meldung)}</span>}
                  {darf('zuordnen') && z.vorschlag && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="schmal"
                        disabled={laeuft || ordnetZu !== null}
                        onClick={() => zuordnen(z)}
                      >
                        {t(ordnetZu === z.lokalnummer ? 'import.ordnetzu' : 'import.zuordnen', {
                          konto: z.vorschlagbezeichnung ?? z.vorschlag,
                        })}
                      </button>
                      {/* Jeder Vorschlag nennt seine Quelle (R138) — sonst wird geklickt statt gelesen. */}
                      {z.vorschlagquelle && <span className="gedaempft"> ({z.vorschlagquelle})</span>}
                    </>
                  )}
                  {/*
                    **Auch die Zeile ohne Vorschlag hat einen Weg** (R589): Sie
                    ist der Fall, um den es geht. Der Knopf führt ins Fenster,
                    das alle offenen Konten der Lieferung trägt — nicht in
                    einen anderen Bereich.
                  */}
                  {darf('zuordnen') && z.konto === null && z.lokalnummer.length > 0 && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="ortsverweis"
                        disabled={laeuft || ordnetZu !== null}
                        onClick={() => setFensterOffen(true)}
                      >
                        {t('import.zuordnung.zeile')}
                      </button>
                    </>
                  )}
                </td>
                <td className="zahl">{z.betrag === null ? '—' : <Betrag wert={z.betrag} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        **Gelesen wird einmal, beim Schließen** (R591) — und nur, wenn etwas
        geschrieben wurde. Wer das Fenster öffnet und wieder zumacht, wartet
        nicht auf eine Rechnung, an der sich nichts geändert hat.
      */}
      {fensterOffen && (
        <Zuordnungsfenster
          vorschau={vorschau}
          einheit={einheit}
          periode={periode}
          aufSchliessen={(geschrieben) => {
            setFensterOffen(false)
            if (geschrieben) {
              aufNeuPruefen()
            }
          }}
        />
      )}
    </div>
  )
}

function Zahlkachel({ name, wert }: { name: string; wert: number }) {
  return (
    <div className="kennzahl">
      <span className="name">{name}</span>
      <span className="wert">{wert}</span>
    </div>
  )
}
